import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { bulkSyncBridge } from '@/tauri/chronicle/bulk-sync';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

type DrainTrigger = 'startup' | 'network_reconnect';

interface UseRecoveryDrainArgs {
  /** Non-null once authenticated; supplies the userId to drain for. */
  bulkSyncAuth: { userId: string } | null;
  isBulkSyncAuthReady: boolean;
  /** Set by the provider once the engine has a token; the drain no-ops until then. */
  initialAuthProvisionedRef: MutableRefObject<boolean>;
}

/**
 * Device-independent offline-cache recovery drain — re-uploads `upload_failed`
 * rows from the local copy (no Chronicle device needed). Returns a stable
 * `runRecoveryDrain` the provider calls on startup; also self-fires on network
 * reconnect.
 */
export function useRecoveryDrain({
  bulkSyncAuth,
  isBulkSyncAuthReady,
  initialAuthProvisionedRef,
}: UseRecoveryDrainArgs) {
  const drainInFlightRef = useRef(false);

  const runRecoveryDrain = useCallback(
    async (trigger: DrainTrigger) => {
      if (!isTauri() || !bulkSyncAuth || !initialAuthProvisionedRef.current) return;
      // Single-flight: a reconnect storm or an overlap with the startup run
      // must not fan out duplicate drains.
      if (drainInFlightRef.current) return;
      drainInFlightRef.current = true;
      try {
        const recovered = await bulkSyncBridge.retryFailed(bulkSyncAuth.userId);
        logger.info({
          msg: '[bulk_sync] Offline-cache recovery drain complete',
          trigger,
          recovered,
        });
      } catch (error) {
        logger.warn({
          msg: '[bulk_sync] Offline-cache recovery drain failed',
          trigger,
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        drainInFlightRef.current = false;
      }
    },
    [bulkSyncAuth, initialAuthProvisionedRef]
  );

  // Re-run the drain when the network comes back (upload failed while offline,
  // connectivity returns while the app stays open). No-ops until engine + token.
  useEffect(() => {
    if (!isTauri() || !isBulkSyncAuthReady) return;
    const onOnline = () => {
      void runRecoveryDrain('network_reconnect');
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [isBulkSyncAuthReady, runRecoveryDrain]);

  return runRecoveryDrain;
}
